'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { Button } from '@components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@components/ui/form';
import { Input, PasswordInput } from '@components/ui/input';
import Link from '@components/ui/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { REAUTH_REQUIRED_PARAM } from '@lib/auth/constants/reauth-required-param';
import { REDIRECTED_FROM_PARAM } from '@lib/auth/constants/redirected-from-param';
import { useClerkStatus } from '@lib/auth/hooks/use-clerk-degraded';
import { useClerkKillSwitch } from '@lib/auth/hooks/use-clerk-kill-switch';
import { useSendVerificationCode } from '@lib/auth/hooks/use-send-verification-code';
import { useSignIn } from '@lib/auth/hooks/use-signin';
import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { withRedirectedFrom } from '@lib/auth/utils/with-redirected-from';
import { ALL_ROUTES } from '@lib/constants/apis';
import { AUTH_ANALYTICS_EVENTS } from '@lib/observability/auth/constants';
import { captureEvent } from '@lib/observability/events/captureEvent';
import { getLogger } from '@lib/observability/logging/log.client';
import { resolvePostSignupRedirect } from '@lib/org-invite-links/utils/resolve-post-signup-redirect';
import { validEmail } from '@lib/shared/utils/valid-email-schema';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';
import { AuthUnavailable } from '@/app/(public)/(auth)/_components/auth-unavailable';
import { GoogleOneTapPrompt } from '@/app/(public)/(auth)/_components/google-one-tap-prompt';
import { GoogleSignInButton } from '@/app/(public)/(auth)/_components/google-sign-in-button';
import ErrorIndicator from '@/components/ui/error-indicator';
import { LoginEmailCodeStep } from './_components/login-email-code-step';
import { redirectedFromHasSubmissionId } from './_components/redirected-from-has-submission-id';
const {
  logError
} = getLogger('app/(public)/(auth)/login/page');

// Hydration gate, matching /signup: false on the server and the
// first client render, true afterward. Without this, Clerk's
// `status` (and password-manager DOM edits on the form) can
// diverge between SSR HTML and the first client paint, which
// Sentry Replay records as PORTAL-16 hydration errors on /login.
const subscribeToHydration = () => () => undefined;
const getHydratedSnapshot = () => true;
const getServerSnapshot = () => false;
function useHasHydrated() {
  return useSyncExternalStore(subscribeToHydration, getHydratedSnapshot, getServerSnapshot);
}

// Form validation schema
const loginSchema = z.object({
  email: validEmail('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});
type LoginFormValues = z.infer<typeof loginSchema>;
type LoginStep = 'credentials' | 'email-code';
function LoginLoadingState() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div><Image src="/assets/images/logo.png" className="mx-auto mb-8 h-8 w-8" width={32} height={32} alt="logo" data-sentry-element="Image" data-sentry-source-file="page.tsx" /><div className="bg-muted mx-auto h-7 w-40 animate-pulse rounded md:h-8" /><div className="bg-muted mx-auto mt-2 h-4 w-52 animate-pulse rounded" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mt-8"><div className="border-muted h-10 w-full animate-pulse rounded-lg border bg-gray-50" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-6 flex items-center gap-2"><span className="bg-muted h-px flex-1" /><div className="bg-muted h-4 w-24 animate-pulse rounded" /><span className="bg-muted h-px flex-1" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mt-6 space-y-3"><div className="space-y-2"><div className="border-muted h-10 w-full animate-pulse rounded-lg border bg-gray-50" /><div className="border-muted h-10 w-full animate-pulse rounded-lg border bg-gray-50" /></div><div className="bg-muted mt-2 h-10 w-full animate-pulse rounded-lg" /><div className="bg-muted mx-auto mt-2 h-5 w-28 animate-pulse rounded" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mx-auto flex w-full max-w-md flex-col px-4 md:px-8" data-sentry-component="LoginLoadingState" data-sentry-source-file="page.tsx">{t0}{t1}{t2}{t3}<div className="mt-8 text-center"><div className="bg-muted mx-auto h-4 w-44 animate-pulse rounded" /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function LoginForm() {
  const $ = _c(75);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectedFrom = searchParams.get(REDIRECTED_FROM_PARAM);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(REAUTH_REQUIRED_PARAM);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reauthRequired = t0 != null;
  const {
    signIn,
    isLoading,
    error
  } = useSignIn();
  const {
    isDegraded: isClerkDegraded,
    isClerkLoading
  } = useClerkStatus();
  const clerkKillSwitch = useClerkKillSwitch();
  const hasHydrated = useHasHydrated();
  const {
    isLoaded: isAuthLoaded,
    isSignedIn
  } = useAuth();
  const sendCode = useSendVerificationCode();
  const [step, setStep] = useState("credentials");
  const [codeEmail, setCodeEmail] = useState("");
  const postSignupHref = resolvePostSignupRedirect(redirectedFrom);
  let t1;
  let t2;
  if ($[2] !== isAuthLoaded || $[3] !== isSignedIn || $[4] !== reauthRequired || $[5] !== redirectedFrom || $[6] !== router) {
    t1 = () => {
      if (isAuthLoaded && isSignedIn && !reauthRequired) {
        router.replace(sanitizeRedirectUrl(redirectedFrom));
      }
    };
    t2 = [isAuthLoaded, isSignedIn, reauthRequired, redirectedFrom, router];
    $[2] = isAuthLoaded;
    $[3] = isSignedIn;
    $[4] = reauthRequired;
    $[5] = redirectedFrom;
    $[6] = router;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      resolver: zodResolver(loginSchema),
      defaultValues: {
        email: "",
        password: ""
      }
    };
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const form = useForm(t3);
  let t4;
  if ($[10] !== redirectedFrom || $[11] !== signIn) {
    t4 = async function onSubmit(values) {
      try {
        await signIn({
          email: values.email,
          password: values.password
        }, sanitizeRedirectUrl(redirectedFrom));
      } catch {}
    };
    $[10] = redirectedFrom;
    $[11] = signIn;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onSubmit = t4;
  let t5;
  if ($[13] !== form || $[14] !== redirectedFrom || $[15] !== sendCode || $[16] !== setCodeEmail || $[17] !== setStep) {
    t5 = async function onSendEmailCode() {
      const isEmailValid = await form.trigger("email");
      if (!isEmailValid) {
        return;
      }
      const email = form.getValues("email");
      captureEvent(AUTH_ANALYTICS_EVENTS.LOGIN_EMAIL_CODE_REQUESTED, {
        has_submission_id: redirectedFromHasSubmissionId(redirectedFrom)
      });
      ;
      try {
        await sendCode.mutateAsync({
          email,
          redirectedFrom,
          includeMagicLink: true
        });
        setCodeEmail(email);
        setStep("email-code");
        toast.success("Verification code sent to your email");
      } catch (t6) {
        const err = t6;
        logError("Failed to send email sign-in code", err as Error);
        toast.error(err instanceof Error ? err.message : "Failed to send verification code");
      }
    };
    $[13] = form;
    $[14] = redirectedFrom;
    $[15] = sendCode;
    $[16] = setCodeEmail;
    $[17] = setStep;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const onSendEmailCode = t5;
  if (!hasHydrated || isClerkLoading) {
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <LoginLoadingState />;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  if (clerkKillSwitch || isClerkDegraded) {
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AuthUnavailable action="Sign-in" />;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    return t6;
  }
  if (step === "email-code") {
    let t6;
    if ($[21] !== setStep) {
      t6 = () => setStep("credentials");
      $[21] = setStep;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== codeEmail || $[24] !== redirectedFrom || $[25] !== t6) {
      t7 = <div className="mx-auto flex w-full max-w-md flex-col px-4 md:px-8"><LoginEmailCodeStep email={codeEmail} redirectedFrom={redirectedFrom} onBack={t6} /></div>;
      $[23] = codeEmail;
      $[24] = redirectedFrom;
      $[25] = t6;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    return t7;
  }
  let t6;
  if ($[27] !== redirectedFrom) {
    t6 = sanitizeRedirectUrl(redirectedFrom);
    $[27] = redirectedFrom;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== postSignupHref || $[30] !== t6) {
    t7 = <GoogleOneTapPrompt signInRedirectUrl={t6} signUpRedirectUrl={postSignupHref} data-sentry-element="GoogleOneTapPrompt" data-sentry-source-file="page.tsx" />;
    $[29] = postSignupHref;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div><Image src="/assets/images/logo.png" className="mx-auto mb-8 h-8 w-8" width={32} height={32} alt="logo" data-sentry-element="Image" data-sentry-source-file="page.tsx" /><h1 className="font-ivar text-center text-xl font-normal text-gray-800 md:text-[28px]">Welcome back!</h1><p className="mt-2 text-center text-xs text-gray-700 md:text-sm">Sign in to your account to proceed</p></div>;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== postSignupHref || $[34] !== redirectedFrom) {
    t9 = <div className="mt-8"><GoogleSignInButton redirectedFrom={redirectedFrom} signUpRedirectUrl={postSignupHref} className="h-10 font-normal" data-sentry-element="GoogleSignInButton" data-sentry-source-file="page.tsx">Continue with Google</GoogleSignInButton></div>;
    $[33] = postSignupHref;
    $[34] = redirectedFrom;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-300" /></div>;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="mt-6"><div className="relative">{t10}<div className="relative flex justify-center text-sm"><span className="bg-white px-2 text-gray-500">Or continue with</span></div></div></div>;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== form || $[39] !== onSubmit) {
    t12 = form.handleSubmit(onSubmit);
    $[38] = form;
    $[39] = onSubmit;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== form.control) {
    t13 = <div className="space-y-2"><FormField control={form.control} name="email" render={_temp} data-sentry-element="FormField" data-sentry-source-file="page.tsx" /><FormField control={form.control} name="password" render={_temp2} data-sentry-element="FormField" data-sentry-source-file="page.tsx" /></div>;
    $[41] = form.control;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== error) {
    t14 = error && <ErrorIndicator variant="compact" message={error} />;
    $[43] = error;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const t15 = isLoading || sendCode.isPending;
  const t16 = isLoading ? "Signing In..." : "Sign In";
  let t17;
  if ($[45] !== t15 || $[46] !== t16) {
    t17 = <Button type="submit" disabled={t15} className="mt-2 w-full" size="lg" data-sentry-element="Button" data-sentry-source-file="page.tsx">{t16}</Button>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const t18 = isLoading || sendCode.isPending;
  const t19 = sendCode.isPending ? "Sending code..." : "Sign in with an email code";
  let t20;
  if ($[48] !== onSendEmailCode || $[49] !== t18 || $[50] !== t19) {
    t20 = <Button type="button" variant="outline" disabled={t18} onClick={onSendEmailCode} className="w-full" size="lg" data-sentry-element="Button" data-sentry-source-file="page.tsx">{t19}</Button>;
    $[48] = onSendEmailCode;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== redirectedFrom) {
    t21 = withRedirectedFrom(ALL_ROUTES.FORGOT_PASSWORD, redirectedFrom);
    $[52] = redirectedFrom;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21) {
    t22 = <Link className="hover:bg-gray-150 mt-2 block rounded-lg p-2.5 text-center text-sm font-medium text-gray-800" href={t21} data-sentry-element="Link" data-sentry-source-file="page.tsx">Forgot Password?</Link>;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t12 || $[57] !== t13 || $[58] !== t14 || $[59] !== t17 || $[60] !== t20 || $[61] !== t22) {
    t23 = <form onSubmit={t12} className="mt-6 space-y-3">{t13}{t14}{t17}{t20}{t22}</form>;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t17;
    $[60] = t20;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== form || $[64] !== t23) {
    t24 = <Form {...form} data-sentry-element="Form" data-sentry-source-file="page.tsx">{t23}</Form>;
    $[63] = form;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== redirectedFrom) {
    t25 = withRedirectedFrom(ALL_ROUTES.SIGNUP, redirectedFrom);
    $[66] = redirectedFrom;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t25) {
    t26 = <p className="mt-8 text-center text-sm text-gray-600">Don't have an account?{" "}<Link className="font-medium text-gray-800 hover:underline" href={t25} data-sentry-element="Link" data-sentry-source-file="page.tsx">Sign Up</Link></p>;
    $[68] = t25;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== t24 || $[71] !== t26 || $[72] !== t7 || $[73] !== t9) {
    t27 = <div className="mx-auto flex w-full max-w-md flex-col px-4 md:px-8" data-sentry-component="LoginForm" data-sentry-source-file="page.tsx">{t7}{t8}{t9}{t11}{t24}{t26}</div>;
    $[70] = t24;
    $[71] = t26;
    $[72] = t7;
    $[73] = t9;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  return t27;
}
function _temp2(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel className="sr-only text-gray-700">Password</FormLabel><FormControl><PasswordInput placeholder="Password" autoComplete="current-password" size="lg" {...field_0} /></FormControl><FormMessage noMinHeight={true} className="text-xs" /></FormItem>;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel className="sr-only text-gray-700">Email address</FormLabel><FormControl><Input type="email" placeholder="Email" autoComplete="email" size="lg" {...field} /></FormControl><FormMessage noMinHeight={true} className="text-xs" /></FormItem>;
}
export default function LoginPage() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LoginLoadingState />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Suspense fallback={t0} data-sentry-element="Suspense" data-sentry-component="LoginPage" data-sentry-source-file="page.tsx"><LoginForm data-sentry-element="LoginForm" data-sentry-source-file="page.tsx" /></Suspense>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}